import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Box, Chip, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { ModelsIcon } from '../global/CustomIcons';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { fetchRequest as fetchBlueprints } from '../../redux/actions/blueprints/list';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';

const formatDate = (d) => {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch (_e) { return '—'; }
};

const safeRelative = (d) => {
  try { if (!d) return ''; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return ''; }
};

// Friendly labels for the blueprint ``kind``: custom architectures,
// foundation fine-tune recipes (any family, predictfm included) and
// BYOM uploads.
const KIND_META = {
  custom:     { label: 'Custom',     tone: 'primary' },
  foundation: { label: 'Foundation', tone: 'success' },
  byom:       { label: 'BYOM',       tone: 'warning' },
};

const KIND_FILTER_OPTIONS = Object.entries(KIND_META).map(
  ([value, { label }]) => ({ value, label })
);

const summariseBlueprint = (bp) => {
  const kind = bp.kind || 'custom';
  if (kind === 'foundation') {
    const r = bp.foundation || {};
    const slug = r.slug || r.backbone_version;
    return {
      type: `Foundation ${r.family || ''}`.trim() || 'Foundation',
      sub: slug ? `Model: ${slug}` : 'Model: —',
      extras: [
        r.intensity && `Intensity: ${r.intensity}`,
        r.lookback && `Lookback ${r.lookback}`,
        r.horizon && `Horizon ${r.horizon}`,
      ].filter(Boolean),
    };
  }
  if (kind === 'byom') {
    const r = bp.byom || {};
    return {
      type: `BYOM ${r.framework || ''}`.trim() || 'BYOM',
      sub: r.model_uri ? `Upload: ${r.model_uri}` : 'Upload: —',
      extras: [],
    };
  }
  // custom
  const fw = bp?.metadata?.framework
    ? `${bp.metadata.framework} v${bp.metadata.framework_version || ''}`.trim()
    : '—';
  return {
    type: bp?.model_blueprint?.model_type || '—',
    sub: `Framework: ${fw}`,
    extras: [],
  };
};

const BlueprintsPage = ({ fetchBlueprints, blueprints, isLoading, error }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState([]);
  const [visibilityFilter, setVisibilityFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => { fetchBlueprints(); }, [fetchBlueprints]);
  useEffect(() => { setPage(0); }, [search, kindFilter, visibilityFilter]);

  const list = blueprints || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((bp) => {
      if (kindFilter.length > 0 && !kindFilter.includes(bp.kind || 'custom')) return false;
      if (visibilityFilter.length > 0
          && !visibilityFilter.includes(String(bp.visibility || '').toLowerCase())) return false;
      if (!q) return true;
      return (bp.name || '').toLowerCase().includes(q)
        || (bp.description || '').toLowerCase().includes(q)
        || (bp.uid || '').toLowerCase().includes(q);
    });
  }, [list, search, kindFilter, visibilityFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDirection === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av; let bv;
      switch (sortColumn) {
        case 'name': av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
        case 'created_at':
        default:
          av = a.created_at ? new Date(a.created_at).getTime() : 0;
          bv = b.created_at ? new Date(b.created_at).getTime() : 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortColumn, sortDirection]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const toggleKind = (k) =>
    setKindFilter((prev) => prev.includes(k) ? prev.filter((v) => v !== k) : [...prev, k]);
  const toggleVisibility = (v) =>
    setVisibilityFilter((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const activeFilterCount = (search ? 1 : 0)
    + (kindFilter.length > 0 ? 1 : 0)
    + (visibilityFilter.length > 0 ? 1 : 0);
  const clearAll = () => {
    setSearch('');
    setKindFilter([]);
    setVisibilityFilter([]);
  };

  const handleAddNewBlueprint = () => navigate('/models/create');
  const handleUploadModel = () => navigate('/models/upload');
  const handleBrowsePublic = () => navigate('/browse-public-models');
  const handleViewBlueprint = (id) => navigate(`/model/${id}`);

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (bp) => {
        const kind = bp.kind || 'custom';
        const meta = KIND_META[kind] || { label: kind, tone: 'neutral' };
        return <EntityCell id={bp.uid} tone={meta.tone} label={meta.label} />;
      },
    },
    {
      key: 'name',
      label: 'Model',
      sortable: true,
      width: '32%',
      render: (bp) => {
        const isPublic = String(bp.visibility).toLowerCase() === 'public';
        const Icon = isPublic ? VisibilityIcon : LockOutlinedIcon;
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{
                fontWeight: 600, fontSize: 13, lineHeight: 1.3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: 360,
              }}>
                {bp.name || '—'}
              </Typography>
              <Tooltip title={isPublic ? 'Public' : 'Private'} arrow placement="top">
                <Icon sx={{
                  fontSize: 13,
                  color: isPublic ? theme.palette.success.main : theme.palette.text.disabled,
                  flexShrink: 0,
                }} />
              </Tooltip>
            </Box>
            {bp.description && (
              <Typography variant="caption" color="text.secondary" sx={{
                display: 'block', fontSize: 11, mt: 0.25,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: 480,
              }}>
                {bp.description}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      width: '28%',
      render: (bp) => {
        const summary = summariseBlueprint(bp);
        return (
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }} noWrap title={summary.type}>
              {summary.type}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{
              fontSize: 11, display: 'block',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 360,
            }} title={summary.sub}>
              {summary.sub}
            </Typography>
            {summary.extras.length > 0 && (
              <Box sx={{ mt: 0.4, display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                {summary.extras.slice(0, 3).map((label, i) => (
                  <Box
                    key={i}
                    component="span"
                    sx={{
                      fontSize: 10,
                      px: 0.5,
                      py: 0.1,
                      borderRadius: 0.5,
                      bgcolor: alpha(theme.palette.text.primary, 0.05),
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 130,
      render: (bp) => (
        <Tooltip title={formatDate(bp.created_at)} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {formatDate(bp.created_at)}
            </Typography>
            {bp.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {safeRelative(bp.created_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      align: 'right',
      stopRowClick: true,
      render: (bp) => (
        <RowActions onOpen={() => handleViewBlueprint(bp.uid)} openLabel="Open model" />
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Models', icon: <ModelsIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
          <Typography variant="h6">Error</Typography>
          <Typography>{String(error)}</Typography>
        </Box>
      </Box>
    );
  }

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search models by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Kind
        </Typography>
        {KIND_FILTER_OPTIONS.map((opt) => {
          const selected = kindFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleKind(opt.value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Visibility
        </Typography>
        {[
          { value: 'public',  label: 'Public'  },
          { value: 'private', label: 'Private' },
        ].map((opt) => {
          const selected = visibilityFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleVisibility(opt.value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <>
      <CMButtonOutlined onClick={handleBrowsePublic}>Public Models</CMButtonOutlined>
      <CMButtonOutlined startIcon={<UploadFileOutlinedIcon />} onClick={handleUploadModel}>
        Upload Model
      </CMButtonOutlined>
      <CMButton startIcon={<AddIcon />} onClick={handleAddNewBlueprint}>
        Model Studio
      </CMButton>
    </>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Models', icon: <ModelsIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Reusable models — custom architectures, foundation models, and BYOM uploads. Pair one with a segment in a Pipeline to start training.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={activeFilterCount > 0 ? `${sorted.length} of ${list.length}` : null}
      />

      <DataTable
        columns={columns}
        rows={paged}
        getRowKey={(row) => row.uid || row._id}
        onRowClick={(row) => handleViewBlueprint(row.uid)}
        loading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={sorted.length}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        emptyState={{
          icon: <ModelsIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No models match your filters' : 'No Models Yet',
          message: activeFilterCount > 0
            ? 'Try a different search term or clear the filters.'
            : "You haven't created any models yet. Open Model Studio to design one from scratch, or browse Public Models for ready-to-use foundation models and shared architectures.",
          action: activeFilterCount > 0 ? null : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CMButtonOutlined onClick={handleBrowsePublic}>Public Models</CMButtonOutlined>
              <CMButton startIcon={<AddIcon />} onClick={handleAddNewBlueprint}>
                Open Model Studio
              </CMButton>
            </Box>
          ),
        }}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  blueprints: state.blueprints_list.response.models,
  isLoading: state.blueprints_list.isLoading,
  error: state.blueprints_list.error,
});

const mapDispatchToProps = (dispatch) => ({
  fetchBlueprints: () => dispatch(fetchBlueprints()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BlueprintsPage);
